import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode, PointerEvent as ReactPointerEvent } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, ChevronsUpDown, Loader2 } from "lucide-react";
import { confirm } from "./ConfirmDialog";
import { EmptyState } from "./EmptyState";
import type { FieldDef, ListColumn, ListRowButton, RecordData, ServerRecordGroup } from "../core";
import { bucketKey, commandSignature, formatBucket, parseGroupByEntry } from "../core";
import { groupKeyOf, readFieldValue, refLabel, writeFieldValue } from "./cell_utils";
import { ColumnChooser } from "./ColumnChooser";
import { appIcon } from "./icons";
import { useLocale } from "../react/providers/LocaleProvider";
import { usePendingCommand } from "../react/hooks/usePendingCommand";
import { widgets } from "../widgets";
import type { ListCellContext, WidgetSlot } from "../widgets";
import { evalRowInvisible } from "../widgets/rowVisibility";

const recId = (rec: RecordData): string => String(rec.id ?? rec.record_uuid);

// Best-effort human label for a row — used as the `data-record-name` e2e hook so
// tests can address a row by its visible identity rather than a uuid.
const recName = (rec: RecordData): string =>
    String(rec.name ?? rec.title ?? rec.display_name ?? rec.record_name ?? "");

function parseOrder(order?: string): [string | null, "asc" | "desc"] {
    if (!order) return [null, "asc"];
    const [field, dir] = order.split(/\s+/);
    return [field || null, dir === "desc" ? "desc" : "asc"];
}

// Tracks the single cell currently in edit mode. Keyed by (recordId, fieldName)
// because field names are unique within the row and recordId disambiguates
// across rows. Null = no editing.
interface EditingCell {
    recordId: string;
    fieldName: string;
}

interface ListViewProps {
    columns: ListColumn[];
    fieldDefs: FieldDef[];
    records: RecordData[];
    isLoading: boolean;
    hidden?: Set<string>;
    onToggleColumn: (name: string) => void;
    selectedIds: Set<string>;
    onToggleRow: (id: string) => void;
    onToggleAll: () => void;
    order?: string;
    onSort: (field: string) => void;
    // Ordered list of group-by fields (outermost first). Empty = flat list.
    groupBy?: string[];
    // Server-grouped payload (presentation.action_records grouped path): EVERY
    // group of the first group-by axis with its true count and one page of
    // records — enum/reference axes include empty groups. When present, the
    // level-0 grouping renders from here; deeper levels still bucket
    // client-side within each group's records. Null/undefined = legacy
    // client-side bucketing over the flat `records` page.
    serverGroups?: ServerRecordGroup[] | null;
    onOpenRecord: (id: string) => void;
    // Row-action buttons declared via <button> under <ListView>. Each renders
    // as a small icon button in the trailing actions cell of every row. The
    // `actionSlug` is required so each button can subscribe to its own RUN_BUTTON
    // signature in the central pending registry (independent per row).
    rowButtons?: ListRowButton[];
    onRowButton?: (button: ListRowButton, record: RecordData) => void;
    actionSlug?: string;
    // Stable key (typically `modelKey` or the action slug) used to scope the
    // column-width sessionStorage entry so widths persist per-view but stay
    // independent across actions.
    storageKey?: string;
    // Generic inline-write callback — any widget (boolean toggle, inline-edit
    // text fields) can update one field on one record by calling onWriteField
    // from its ListCellContext. ListView forwards the call to ActionManager
    // which dispatches record.update + refetches.
    onWriteField?: (record: RecordData, fieldName: string, value: unknown) => void;
    // DSL `editable="top"` / `editable="bottom"` — when set, double-clicking a
    // cell whose widget supplies a `renderEdit` enters inline-edit mode for
    // that cell. Empty string = read-only list (no edit affordance).
    editable?: "top" | "bottom" | "";
}

// One per-row action button. Subscribes to the central pending registry under
// the SAME RUN_BUTTON signature that ActionManager.onRowButton dispatches with,
// so the spinner state is in lock-step with the bus. Each row's button manages
// its own pending state — two rows can run the same button concurrently with
// independent spinners.
function RowActionButton({
    button,
    record,
    actionSlug,
    onClick,
}: {
    button: ListRowButton;
    record: RecordData;
    actionSlug: string;
    onClick?: (b: ListRowButton, rec: RecordData) => void;
}): ReactNode {
    const Icon = appIcon(button.icon ?? undefined);
    const recordUuid = String(record.id ?? record.record_uuid ?? "");
    const signature = commandSignature({
        type: "RUN_BUTTON",
        payload: {
            actionSlug,
            recordUuid,
            commandName: button.command ?? "",
            commandType: button.commandType ?? null,
        },
    });
    const pending = usePendingCommand(signature);
    const handle = async () => {
        if (pending) return;
        if (button.confirm) {
            const ok = await confirm({
                title: button.label || button.name,
                message: button.confirm,
                confirmLabel: button.label || "Confirm",
                cancelLabel: "Cancel",
            });
            if (!ok) return;
        }
        onClick?.(button, record);
    };
    return (
        <button
            type="button"
            className="row-actions__btn"
            title={button.label ?? button.name}
            aria-label={button.label ?? button.name}
            data-viewid="ede-list-row-action"
            data-action={button.name}
            disabled={pending}
            onClick={handle}
        >
            {pending ? <Loader2 size={14} className="row-actions__spinner" /> : <Icon size={14} />}
        </button>
    );
}

const MIN_COL_WIDTH = 60;

// Column names that traditionally identify a record — when present, the
// rendered cell value gets the `is-record-name` modifier so it reads as the
// row's primary title. Long-term this should come from the model's
// `__ede_record_name_field__` plumbed through the list RenderPlan.
const RECORD_NAME_COLS = new Set(["name", "title", "display_name", "record_name"]);

// Per-column pixel widths persisted in sessionStorage keyed by `storageKey`.
// Width changes update both React state (immediate visual response) and
// session storage (survives navigation within the session, dropped on tab
// close — matches the canonical behaviour).
function useColumnWidths(storageKey: string | undefined): {
    widths: Record<string, number>;
    setColumnWidth: (name: string, width: number) => void;
} {
    const fullKey = storageKey ? `list-col-widths:${storageKey}` : null;
    const [widths, setWidths] = useState<Record<string, number>>(() => {
        if (!fullKey) return {};
        try {
            const raw = typeof window !== "undefined" ? window.sessionStorage.getItem(fullKey) : null;
            const parsed = raw ? JSON.parse(raw) : {};
            return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    });

    // Reset state when the storageKey changes (different action loaded).
    useEffect(() => {
        if (!fullKey) {
            setWidths({});
            return;
        }
        try {
            const raw = typeof window !== "undefined" ? window.sessionStorage.getItem(fullKey) : null;
            const parsed = raw ? JSON.parse(raw) : {};
            setWidths(parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {});
        } catch {
            setWidths({});
        }
    }, [fullKey]);

    const setColumnWidth = useCallback(
        (name: string, width: number) => {
            setWidths((prev) => {
                const next = { ...prev, [name]: Math.max(MIN_COL_WIDTH, Math.round(width)) };
                if (fullKey) {
                    try {
                        window.sessionStorage.setItem(fullKey, JSON.stringify(next));
                    } catch {
                        // ignore storage failure (quota / private mode)
                    }
                }
                return next;
            });
        },
        [fullKey],
    );

    return { widths, setColumnWidth };
}

// Pure presentational table: selection, click-to-sort, single-level grouping,
// and row-click → open record. Fed by ActionManager; no HTTP/navigation here.
export function ListView({
    columns,
    fieldDefs,
    records,
    isLoading,
    hidden,
    onToggleColumn,
    selectedIds,
    onToggleRow,
    onToggleAll,
    order,
    onSort,
    groupBy,
    serverGroups = null,
    onOpenRecord,
    rowButtons = [],
    onRowButton,
    actionSlug = "",
    storageKey,
    onWriteField,
    editable = "",
}: ListViewProps) {
    // Per-cell edit state — null when nothing is being edited.
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const isEditableList = editable === "top" || editable === "bottom";
    const { widths: colWidths, setColumnWidth } = useColumnWidths(storageKey);

    // When the user toggles an optional column from hidden → visible via the
    // ColumnChooser, briefly pulse the newly-visible column so their eye
    // catches the addition (otherwise the new column just appears mid-table
    // with no signal). Compare the prior `hidden` Set against the current one
    // each render — the column whose name disappeared from the Set is the one
    // that just became visible.
    const prevHiddenRef = useRef<Set<string>>(hidden ?? new Set());
    const [recentlyShown, setRecentlyShown] = useState<string | null>(null);
    useEffect(() => {
        const prev = prevHiddenRef.current;
        const curr = hidden ?? new Set<string>();
        // Content-equality guard — when the parent recreates the `hidden` Set
        // every render with the same content (which Sets do via `new Set(...)`
        // even when nothing toggled), the effect would otherwise re-fire and
        // accidentally retrigger setRecentlyShown / refresh the ref, which in
        // turn triggers another parent render. Skip entirely when content is
        // identical.
        let same = prev.size === curr.size;
        if (same) {
            for (const n of prev) {
                if (!curr.has(n)) { same = false; break; }
            }
        }
        if (same) return;
        let justShown: string | null = null;
        for (const name of prev) {
            if (!curr.has(name)) {
                justShown = name;
                break;
            }
        }
        prevHiddenRef.current = curr;
        if (justShown) {
            setRecentlyShown(justShown);
            const id = window.setTimeout(() => setRecentlyShown(null), 1200);
            return () => window.clearTimeout(id);
        }
    }, [hidden]);
    // Drag state held in a ref so pointer-move doesn't trigger a re-render
    // per frame; component re-renders only when setColumnWidth commits the
    // new width (debounced naturally by React's batching).
    const resizeRef = useRef<{ name: string; startX: number; startWidth: number; thEl: HTMLTableCellElement } | null>(null);
    const [resizingColumn, setResizingColumn] = useState<string | null>(null);

    const beginResize = (name: string, ev: ReactPointerEvent<HTMLDivElement>) => {
        ev.preventDefault();
        ev.stopPropagation();
        const th = (ev.currentTarget.parentElement as HTMLTableCellElement | null) ?? null;
        if (!th) return;
        const rect = th.getBoundingClientRect();
        resizeRef.current = { name, startX: ev.clientX, startWidth: rect.width, thEl: th };
        setResizingColumn(name);
        ev.currentTarget.setPointerCapture(ev.pointerId);
    };

    const onResizeMove = (ev: ReactPointerEvent<HTMLDivElement>) => {
        const state = resizeRef.current;
        if (!state) return;
        const newWidth = Math.max(MIN_COL_WIDTH, state.startWidth + (ev.clientX - state.startX));
        setColumnWidth(state.name, newWidth);
    };

    const endResize = (ev: ReactPointerEvent<HTMLDivElement>) => {
        if (!resizeRef.current) return;
        try {
            ev.currentTarget.releasePointerCapture(ev.pointerId);
        } catch {
            // pointer may already be released
        }
        resizeRef.current = null;
        setResizingColumn(null);
    };
    const groupByEntries = groupBy ?? [];
    const groupByKey = groupByEntries.join(",");

    // Default-collapsed groups: we track which full paths are EXPANDED, so a
    // path missing from the set means "show only the header". Switching the
    // group-by axes resets the set so paths don't carry stale state.
    const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
    useEffect(() => {
        setExpandedPaths(new Set());
    }, [groupByKey]);

    const fieldMap = useMemo(() => new Map(fieldDefs.map((f) => [f.name, f])), [fieldDefs]);

    // Parse entries into {field, granularity?} once per groupBy change. Field
    // lookup goes against the bare field; granularity drives date bucketing.
    const parsedLevels = useMemo(
        () => groupByEntries.map((e) => parseGroupByEntry(e)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [groupByKey],
    );

    // Per group-by level, a map from bucket-key → human label (only populated
    // when that level's field is an enum/selection). null = use raw key.
    const selectionLabelsPerLevel = useMemo<Array<Map<string, string> | null>>(() => {
        return parsedLevels.map((p) => {
            const def = fieldMap.get(p.field);
            if (!def?.selection?.length) return null;
            return new Map(def.selection);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupByKey, fieldMap]);

    const cols = columns.filter((c) => !c.invisible && !hidden?.has(c.name));
    // Expanded slot list driven by the widget registry. Each ListColumn maps
    // to one or more `WidgetSlot`s — most widgets emit a single `field` slot,
    // `avatar_title` emits `[decoration(avatar), field(title)]`. The registry
    // is the single source of truth: ListView never inspects `column.widget`
    // directly, never hardcodes a renderer.
    const slots = useMemo<WidgetSlot[]>(
        () => cols.flatMap((c) => widgets.slotsFor(c, fieldMap.get(c.name)?.type)),
        [cols, fieldMap],
    );

    // Active locale timezone, threaded into every cell context so datetime
    // widgets can format in the user's zone.
    const { timezone } = useLocale();
    const buildCtx = (rec: RecordData, c: ListColumn): ListCellContext => {
        const field = fieldMap.get(c.name);
        return {
            // Custom-property columns store their value at rec[bag][key], not at
            // the top-level wire name — read/write through the shared helpers.
            value: readFieldValue(rec, field, c.name),
            record: rec,
            column: c,
            field,
            timezone,
            onWriteField: (fieldName, val) =>
                fieldName === c.name
                    ? writeFieldValue(rec, field, c.name, val, (fn, v) => onWriteField?.(rec, fn, v))
                    : onWriteField?.(rec, fieldName, val),
        };
    };
    // Only optional (show/hide) columns are user-toggleable — the trailing
    // toggle column exists only when there's at least one.
    const optionalColumns = columns.filter((c) => c.optional === "show" || c.optional === "hide");
    const hasToggle = optionalColumns.length > 0;
    const hasRowButtons = rowButtons.length > 0;
    // ONE trailing column absorbs both surfaces: header cell hosts the column
    // chooser (if any optional columns), every body cell hosts the row buttons
    // (if any declared). When neither is present the column doesn't render.
    const hasTrailingCol = hasToggle || hasRowButtons;
    // Pixel width for the trailing column. The table uses `table-layout: fixed`
    // so column widths come from the first row of <th>s — we set this inline on
    // the header. 40px fits the chooser button alone; each row button adds
    // ~26px (24 icon + 2 gap); plus the right-padding bleed-room.
    const trailingColWidth = (hasRowButtons ? rowButtons.length * 26 : 0) + (hasToggle ? 32 : 0) + 16;
    const [sortField, sortDir] = parseOrder(order);

    if (records.length === 0 && !serverGroups) {
        return (
            <div className="list-view" data-viewid="ede-list">
                {isLoading ? (
                    <div className="list-view__state">Loading…</div>
                ) : (
                    <EmptyState
                        title="No records to show"
                        hint="Adjust your filters or create a new record to get started."
                    />
                )}
            </div>
        );
    }

    const allRecords = serverGroups ? serverGroups.flatMap((g) => g.records) : records;
    const allIds = allRecords.map(recId);
    const allChecked = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
    const someChecked = !allChecked && allIds.some((id) => selectedIds.has(id));

    const renderRow = (rec: RecordData): ReactNode => {
        const id = recId(rec);
        const checked = selectedIds.has(id);
        return (
            <tr
                key={id}
                className={checked ? "is-selected" : ""}
                onClick={() => onOpenRecord(id)}
                data-viewid="ede-list-row"
                data-record-id={id}
                data-record-name={recName(rec)}
            >
                <td className="col-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        className="checkbox"
                        checked={checked}
                        onChange={() => onToggleRow(id)}
                        aria-label="Select row"
                    />
                </td>
                {slots.map((s, slotIdx) => {
                    const c = s.column;
                    const ctx = buildCtx(rec, c);
                    if (s.kind === "decoration") {
                        return (
                            <td
                                key={s.keyOverride ?? `${c.name}__deco-${slotIdx}`}
                                className="col-avatar"
                            >
                                {s.render(ctx)}
                            </td>
                        );
                    }
                    // Edit-mode dispatch: when the user double-clicks an
                    // editable cell, render the widget's `renderEdit` (which
                    // owns the input + commit/cancel UX) instead of the
                    // read-only render. `widgets.resolve` looks up the same
                    // descriptor `slotsFor` produced, so the edit renderer
                    // matches the cell's display widget exactly.
                    const widget = widgets.resolve(c.widget, fieldMap.get(c.name)?.type);
                    const canEditCell = isEditableList && Boolean(widget?.renderEdit);
                    const isEditingThis =
                        canEditCell && editingCell?.recordId === id && editingCell?.fieldName === c.name;
                    const classes = [
                        c.name === recentlyShown ? "is-just-shown" : "",
                        RECORD_NAME_COLS.has(c.name) ? "is-record-name" : "",
                        canEditCell ? "is-editable" : "",
                        isEditingThis ? "is-editing" : "",
                    ].filter(Boolean).join(" ");
                    return (
                        <td
                            key={c.name}
                            className={classes || undefined}
                            data-viewid="ede-list-cell"
                            data-field={c.name}
                            onDoubleClick={canEditCell && !isEditingThis ? (e) => {
                                e.stopPropagation();
                                setEditingCell({ recordId: id, fieldName: c.name });
                            } : undefined}
                            onClick={isEditingThis ? (e) => e.stopPropagation() : undefined}
                        >
                            {isEditingThis && widget?.renderEdit
                                ? widget.renderEdit({
                                    ...ctx,
                                    commit: (next) => {
                                        // Only write when the value actually changed —
                                        // exiting an unchanged cell shouldn't trip a
                                        // round-trip + refetch.
                                        if (next !== ctx.value) {
                                            onWriteField?.(rec, c.name, next);
                                        }
                                        setEditingCell(null);
                                    },
                                    cancel: () => setEditingCell(null),
                                })
                                : s.render(ctx)}
                        </td>
                    );
                })}
                {hasTrailingCol ? (
                    <td className="col-toggle" onClick={(e) => e.stopPropagation()}>
                        {hasRowButtons ? (
                            <span className="row-actions">
                                {rowButtons
                                    .filter((b) => !evalRowInvisible(b.invisible, rec))
                                    .map((b) => (
                                        <RowActionButton
                                            key={b.name}
                                            button={b}
                                            record={rec}
                                            actionSlug={actionSlug}
                                            onClick={onRowButton}
                                        />
                                    ))}
                            </span>
                        ) : null}
                    </td>
                ) : null}
            </tr>
        );
    };

    // Recursively render N levels of group headers. `level` indexes into
    // parsedLevels; when it reaches the end, the bucket's records render as
    // normal data rows. `parentPath` accumulates the bucket keys traversed so
    // far so each header has a unique expansion identity.
    const renderLevel = (recs: RecordData[], level: number, parentPath: string): ReactNode[] => {
        if (level >= parsedLevels.length) return recs.map(renderRow);
        const { field, granularity } = parsedLevels[level];
        const labels = selectionLabelsPerLevel[level];
        const buckets = new Map<string, RecordData[]>();
        for (const rec of recs) {
            // For date/datetime groupbys, the bucket key is derived from the
            // raw timestamp truncated to the granularity (year / quarter /
            // month / week / day). Otherwise the raw value is the key.
            const raw = rec[field];
            const key = granularity
                ? bucketKey(typeof raw === "string" ? raw : null, granularity)
                : groupKeyOf(raw);
            const bucket = buckets.get(key);
            if (bucket) bucket.push(rec);
            else buckets.set(key, [rec]);
        }
        const rows: ReactNode[] = [];
        for (const [key, bucketRecs] of buckets) {
            const path = parentPath ? `${parentPath}/${key}` : key;
            const isCollapsed = !expandedPaths.has(path);
            // Label resolution: date bucket → locale-aware formatted label
            // ("May 2026"); enum/selection → selection map; otherwise raw key.
            const displayLabel = granularity
                ? formatBucket(typeof bucketRecs[0]?.[field] === "string" ? bucketRecs[0][field] as string : null, granularity)
                : labels?.get(key) ?? key;
            rows.push(
                <tr
                    key={`group:${path}`}
                    className={`list-group list-group--level-${level}`}
                    onClick={() =>
                        setExpandedPaths((prev) => {
                            const next = new Set(prev);
                            if (next.has(path)) next.delete(path);
                            else next.add(path);
                            return next;
                        })
                    }
                >
                    <td colSpan={slots.length + 1 + (hasTrailingCol ? 1 : 0)} className="list-group__cell">
                        <span className="list-group__head" style={{ paddingLeft: level * 18 }}>
                            {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                            <span className="list-group__label">{displayLabel}</span>
                            <span className="list-group__count">{bucketRecs.length}</span>
                        </span>
                    </td>
                </tr>,
            );
            if (!isCollapsed) {
                for (const r of renderLevel(bucketRecs, level + 1, path)) rows.push(r);
            }
        }
        return rows;
    };

    // Server-grouped rendering (level 0 from the grouped payload). Every group
    // of the axis renders — including empty ones (count 0) — with its TRUE
    // count; deeper group-by levels bucket client-side within each group's
    // page of records. When a group holds more rows than its page, a muted
    // footer row says so instead of silently truncating.
    const renderServerGroups = (): ReactNode[] => {
        if (!serverGroups) return [];
        const level0 = parsedLevels[0];
        const labels = selectionLabelsPerLevel[0];
        const rows: ReactNode[] = [];
        for (const g of serverGroups) {
            const key = g.value == null ? "__null__" : String(g.value);
            const path = `srv:${key}`;
            const isCollapsed = !expandedPaths.has(path);
            // Label: server-resolved (enum option / comodel display name) →
            // selection map → first record's reference chip → raw value;
            // the null bucket reads "None".
            const firstValue = level0 ? g.records[0]?.[level0.field] : undefined;
            const displayLabel =
                g.label
                ?? (g.value != null ? labels?.get(String(g.value)) : undefined)
                ?? (g.value == null
                    ? "None"
                    : firstValue != null && typeof firstValue === "object"
                        ? refLabel(firstValue)
                        : String(g.value));
            rows.push(
                <tr
                    key={`group:${path}`}
                    className="list-group list-group--level-0"
                    onClick={() =>
                        setExpandedPaths((prev) => {
                            const next = new Set(prev);
                            if (next.has(path)) next.delete(path);
                            else next.add(path);
                            return next;
                        })
                    }
                >
                    <td colSpan={slots.length + 1 + (hasTrailingCol ? 1 : 0)} className="list-group__cell">
                        <span className="list-group__head">
                            {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                            <span className="list-group__label">{displayLabel}</span>
                            <span className="list-group__count">{g.count}</span>
                        </span>
                    </td>
                </tr>,
            );
            if (!isCollapsed) {
                for (const r of renderLevel(g.records, 1, path)) rows.push(r);
                if (g.count > g.records.length) {
                    rows.push(
                        <tr key={`group:${path}:more`} className="list-group__more">
                            <td colSpan={slots.length + 1 + (hasTrailingCol ? 1 : 0)} className="list-group__more-cell">
                                Showing first {g.records.length} of {g.count}
                            </td>
                        </tr>,
                    );
                }
            }
        }
        return rows;
    };

    return (
        <div className="list-view" data-viewid="ede-list">
            <table className="list-table">
                <thead>
                    <tr>
                        <th className="col-checkbox">
                            <input
                                type="checkbox"
                                className="checkbox"
                                checked={allChecked}
                                ref={(el) => {
                                    if (el) el.indeterminate = someChecked;
                                }}
                                onChange={onToggleAll}
                                aria-label="Select all rows"
                            />
                        </th>
                        {slots.map((s, slotIdx) => {
                            const c = s.column;
                            if (s.kind === "decoration") {
                                // Decoration slot (e.g. avatar tile) — no
                                // label / sort / resize. Width pinned with
                                // BOTH min and max so the browser can't grow
                                // it when the table stretches to min-width:
                                // 100% (extra space goes to the title slot
                                // and other data columns instead).
                                const w = s.widthHint ?? 36;
                                return (
                                    <th
                                        key={s.keyOverride ?? `${c.name}__deco-${slotIdx}`}
                                        className="col-avatar"
                                        style={{ width: w, minWidth: w, maxWidth: w }}
                                        aria-label=""
                                    />
                                );
                            }
                            const isSorted = sortField === c.name;
                            // Width resolution: user-saved width wins. Otherwise
                            // the widget's `listColWidthHint` declared in
                            // builtins.tsx. When NEITHER exists we leave width
                            // unset — the column would absorb any leftover
                            // space when the table stretches to `min-width:
                            // 100%`. Every shipped widget declares a hint —
                            // `table-layout: fixed` + the table's
                            // `min-width: 100%` proportionally scale every
                            // column to fit the container, so no single
                            // column hogs the leftover. A hint-less column
                            // would become a single-column hot-spot — avoid.
                            const saved = colWidths[c.name];
                            const width = saved ?? s.widthHint;
                            const isResizing = resizingColumn === c.name;
                            const isJustShown = c.name === recentlyShown;
                            return (
                                <th
                                    key={c.name}
                                    className={isJustShown ? "is-just-shown" : undefined}
                                    style={width != null ? { width, minWidth: width } : undefined}
                                >
                                    <span className="list-table__head is-sortable" onClick={() => onSort(c.name)}>
                                        {c.label}
                                        <span className="list-table__sort">
                                            {isSorted ? (
                                                sortDir === "desc" ? <ArrowDown size={11} /> : <ArrowUp size={11} />
                                            ) : (
                                                <ChevronsUpDown size={11} />
                                            )}
                                        </span>
                                    </span>
                                    <div
                                        className={`list-table__resizer ${isResizing ? "is-resizing" : ""}`}
                                        onPointerDown={(ev) => beginResize(c.name, ev)}
                                        onPointerMove={onResizeMove}
                                        onPointerUp={endResize}
                                        onPointerCancel={endResize}
                                        onClick={(ev) => ev.stopPropagation()}
                                        aria-hidden="true"
                                    />
                                </th>
                            );
                        })}
                        {hasTrailingCol ? (
                            <th
                                className="col-toggle"
                                style={{ width: trailingColWidth, minWidth: trailingColWidth }}
                            >
                                {hasToggle ? (
                                    <ColumnChooser columns={optionalColumns} hidden={hidden ?? new Set()} onToggle={onToggleColumn} />
                                ) : null}
                            </th>
                        ) : null}
                    </tr>
                </thead>
                <tbody>{serverGroups ? renderServerGroups() : parsedLevels.length > 0 ? renderLevel(records, 0, "") : records.map(renderRow)}</tbody>
            </table>
        </div>
    );
}
